import React from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useConsent } from '@/hooks/useConsent';
import { setAnalyticsConsent } from '@/services/consent';

interface CookieConsentBannerProps {
  /** Opens the Privacy Notice page. */
  onNavigateToPrivacyPolicy?: () => void;
}

/**
 * Consent banner for non-essential analytics.
 *
 * Mounted once in app.tsx, so a visitor who has not decided yet is asked on
 * whatever page they land on and keeps being asked until they answer.
 *
 * Rendered through a portal on document.body rather than in place: the home
 * page runs GSAP ScrollSmoother, which puts a transform on #smooth-content, and
 * a transformed ancestor makes `position: fixed` resolve against that element —
 * the banner would scroll away with the page instead of staying put.
 *
 * Accept and Reject are one click each, same size and weight, so declining is
 * no harder than agreeing. Neither is preselected, and ignoring the banner
 * leaves consent ungranted (see services/consent.ts).
 */
const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onNavigateToPrivacyPolicy,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { promptOpen } = useConsent();

  if (!promptOpen) return null;

  const buttonBase =
    'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-full border px-5 py-2 text-base font-medium transition-colors';

  return ReactDOM.createPortal(
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 z-9999 w-full px-safe pb-safe"
    >
      <div
        className={`mx-4 mb-4 flex max-w-4xl flex-col gap-4 rounded-2xl border p-5 shadow-2xl lg:mx-auto lg:flex-row lg:items-center lg:justify-between ${
          isDarkMode
            ? 'border-gray-600 bg-gray-800 text-white'
            : 'border-gray-300 bg-white text-theme-primary-light'
        }`}
      >
        <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          We&rsquo;d like to use analytics to see how Aleph is used, so we can make it better. It is product analytics only:
          no advertising, and no tracking you across other companies&rsquo; sites.{' '}
          {onNavigateToPrivacyPolicy && (
            <button
              type="button"
              onClick={onNavigateToPrivacyPolicy}
              className={`cursor-pointer underline transition-opacity hover:opacity-80 ${
                isDarkMode ? 'text-white' : 'text-theme-primary-light'
              }`}
            >
              Privacy Notice
            </button>
          )}
        </p>

        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={() => setAnalyticsConsent(false)}
            className={`${buttonBase} ${
              isDarkMode
                ? 'border-blue-400 text-blue-400 hover:bg-blue-900/20'
                : 'border-blue-600 text-theme-primary-light hover:bg-gray-100'
            }`}
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => setAnalyticsConsent(true)}
            className={`${buttonBase} ${
              isDarkMode
                ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                : 'border-blue-700 bg-blue-700 text-white hover:bg-blue-800'
            }`}
          >
            Accept
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CookieConsentBanner;
